import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Play, ExternalLink, Star, Clock, Heart, Share2, Link } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { tryNativeShare } from '../utils/shareHelpers';
import { TrailerPlayer, getYouTubeId } from './TrailerPlayer';

const ShareMenu = ({ title, releaseId, shareType = 'release', onClose }) => {
  const [copied, setCopied] = useState(false);
  // Use the backend's OG-rich preview route so crawlers (WhatsApp / X /
  // iMessage) can build a per-title link card with the poster thumbnail.
  // Real browsers redirect from this URL into the SPA's deep-linked modal.
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/api/share/${shareType}/${encodeURIComponent(releaseId)}`;
  const shareText = `Check out "${title}" on The Movie Junkie Streaming Guide!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
  };

  const handleTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleInstagram = async () => {
    // Instagram blocks deep-link sharing of arbitrary URLs by design. We
    // copy the link first, then attempt to open the Instagram app via the
    // `instagram://` URI scheme. Falls back to instagram.com (which itself
    // redirects into the app on mobile when installed).
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard failures
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      window.location.href = 'instagram://app';
      setTimeout(() => { window.open('https://www.instagram.com/', '_blank'); }, 700);
    } else {
      window.open('https://www.instagram.com/', '_blank', 'noopener');
    }
    onClose();
  };

  return (
    <div className="absolute top-12 right-0 z-20 bg-gray-900 rounded-xl shadow-2xl border border-white/10 p-2 min-w-48">
      <button
        onClick={handleCopyLink}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <Link className="w-5 h-5 text-gray-400" />
        <span className="text-white text-sm">{copied ? 'Copied!' : 'Copy Link'}</span>
      </button>
      <button
        onClick={handleWhatsApp}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-white text-sm">WhatsApp</span>
      </button>
      <button
        onClick={handleInstagram}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-pink-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
        <span className="text-white text-sm">Instagram</span>
      </button>
      <button
        onClick={handleTwitter}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-lg transition-colors text-left"
      >
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
        <span className="text-white text-sm">X (Twitter)</span>
      </button>
    </div>
  );
};

const DetailModal = ({ release, onClose, isInWatchlist, onToggleWatchlist }) => {
  const [showTrailer, setShowTrailer] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const trailerRef = useRef(null);
  const modalRef = useRef(null);
  const { requireSubscription } = useSubscription();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowShareMenu(false);
    if (showShareMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showShareMenu]);

  // Smooth scroll to trailer when it becomes visible
  useEffect(() => {
    if (showTrailer && trailerRef.current && modalRef.current) {
      setTimeout(() => {
        const modalElement = modalRef.current;
        const trailerElement = trailerRef.current;
        const targetPosition = trailerElement.offsetTop - 100;
        
        const startPosition = modalElement.scrollTop;
        const distance = targetPosition - startPosition;
        const duration = 600;
        let start = null;
        
        const easeInOutCubic = (t) => {
          return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };
        
        const animation = (currentTime) => {
          if (start === null) start = currentTime;
          const elapsed = currentTime - start;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = easeInOutCubic(progress);
          
          modalElement.scrollTop = startPosition + (distance * easeProgress);
          
          if (elapsed < duration) {
            requestAnimationFrame(animation);
          }
        };
        
        requestAnimationFrame(animation);
      }, 150);
    }
  }, [showTrailer]);

  const videoId = getYouTubeId(release.trailer_url);
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&playsinline=1` : null;

  const PLATFORM_COLORS = {
    'Netflix': 'bg-[#E50914]',
    'Prime Video': 'bg-[#00A8E1]',
    'Apple TV+': 'bg-black',
    'Hulu': 'bg-[#1CE783]',
    'Disney+': 'bg-[#113CCF]',
    'HBO Max': 'bg-[#9D34DA]'
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Hide the "Watch on <platform>" CTA when the title hasn't been
  // released yet — the link would land users on an empty Prime/Netflix
  // search result. Reminder bell + watchlist still work.
  const isReleaseUpcoming = release?.release_date
    ? new Date(release.release_date) > new Date()
    : false;

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/95 backdrop-blur-sm scroll-smooth"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      data-testid="detail-modal"
    >
      <div className="min-h-full flex items-start justify-center md:p-4">
        <div 
          className="relative w-full max-w-5xl bg-gray-900 rounded-none md:rounded-2xl overflow-hidden shadow-2xl md:my-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header strip with Close and Share — sits ABOVE the iframe so
              the buttons never overlap YouTube's settings/chrome that
              appears on the player's top-right. */}
          <div className="flex items-center justify-end gap-2 px-3 py-2 bg-gray-900 border-b border-white/5">
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  requireSubscription(async () => {
                    // On mobile, prefer the OS share sheet (covers
                    // Instagram Stories, Messages, Mail, etc).
                    const shareUrl = `${window.location.origin}/api/share/release/${encodeURIComponent(release.id)}`;
                    const handled = await tryNativeShare({
                      title: release.title,
                      text: `Check out "${release.title}" on The Movie Junkie Streaming Guide!`,
                      url: shareUrl,
                    });
                    if (!handled) setShowShareMenu(prev => !prev);
                  }, 'share');
                }}
                data-testid="detail-share-btn"
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Share"
              >
                <Share2 className="w-5 h-5 text-white" />
              </button>
              {showShareMenu && (
                <ShareMenu title={release.title} releaseId={release.id} onClose={() => setShowShareMenu(false)} />
              )}
            </div>
            <button
              onClick={onClose}
              data-testid="detail-close-btn"
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Hero Section — autoplay trailer when available (16:9 aspect for
              a big, comfortable view), poster art as the fallback. */}
          <div className="relative bg-black">
            {embedUrl ? (
              <TrailerPlayer trailerUrl={release.trailer_url} title={release.title} testId="detail-trailer-iframe" />
            ) : (
              <div className="relative h-48 md:h-80">
                {release.poster_url ? (
                  <>
                    <img
                      src={release.poster_url}
                      alt={release.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                  </>
                ) : (
                  <div className="w-full h-full bg-gray-800" />
                )}
              </div>
            )}

            {/* Platform badge — only on the poster fallback. */}
            {!embedUrl && (
              <div className={`absolute top-4 left-4 ${PLATFORM_COLORS[release.platform] || 'bg-gray-700'} px-3 py-1 rounded-full`}>
                <span className="text-white text-xs md:text-sm font-bold uppercase tracking-wide">
                  {release.platform}
                </span>
              </div>
            )}

            {/* Title overlay — only on poster fallback. */}
            {!embedUrl && (
              <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 right-4 md:right-6">
                <h2 className="font-primary text-2xl md:text-4xl text-white uppercase tracking-wide mb-2">
                  {release.title}
                </h2>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-gray-300 text-sm md:text-base">
                  <span className="bg-white/20 px-2 md:px-3 py-1 rounded-full">
                    {release.type}
                  </span>
                  {release.language && (
                    <span className="bg-white/20 px-2 md:px-3 py-1 rounded-full">
                      {release.language}
                    </span>
                  )}
                  {release.genres && (
                    <span className="bg-white/20 px-2 md:px-3 py-1 rounded-full">
                      {release.genres}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content Section — tighter horizontal padding so the description
              breathes wider against the side edges. */}
          <div className="px-3 md:px-5 py-4 md:py-6 space-y-4 md:space-y-6">
            {/* Title + badges — rendered here when the trailer occupies the
                hero (overlay UI is suppressed for the iframe). */}
            {embedUrl && (
              <div className="space-y-3">
                <h2 className="font-primary text-2xl md:text-4xl text-white uppercase tracking-wide leading-tight">
                  {release.title}
                </h2>
                <div className="flex flex-wrap items-center gap-2 md:gap-3 text-gray-200 text-sm md:text-base">
                  <span className={`${PLATFORM_COLORS[release.platform] || 'bg-gray-700'} text-white px-3 py-1 rounded-full font-bold uppercase tracking-wide`}>
                    {release.platform}
                  </span>
                  <span className="bg-white/10 px-3 py-1 rounded-full">
                    {release.type}
                  </span>
                  {release.language && (
                    <span className="bg-white/10 px-3 py-1 rounded-full">
                      {release.language}
                    </span>
                  )}
                  {release.genres && (
                    <span className="bg-white/10 px-3 py-1 rounded-full">
                      {release.genres}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Release Info */}
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-4 md:w-5 h-4 md:h-5" />
                <span className="font-mono text-sm md:text-base">{formatDate(release.release_date)}</span>
              </div>
              {release.rating && (
                <div className="flex items-center gap-2">
                  <Star className="w-4 md:w-5 h-4 md:h-5 text-yellow-500" fill="#EAB308" />
                  <span className="font-mono text-sm md:text-base text-white">
                    {release.rating}/10
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            {release.description && (
              <div>
                <h3 data-testid="detail-overview-heading" className="font-secondary text-lg text-white font-bold mb-2 md:mb-3">Overview</h3>
                <p className="font-secondary text-sm md:text-base text-gray-300 leading-relaxed">
                  {release.description}
                </p>
              </div>
            )}

            {/* Cast */}
            {release.cast && (
              <div>
                <h3 data-testid="detail-cast-heading" className="font-secondary text-lg text-white font-bold mb-2 md:mb-3">Cast</h3>
                <p className="font-secondary text-sm md:text-base text-gray-400">
                  {release.cast}
                </p>
              </div>
            )}

            {/* Action Buttons — "Play Trailer" omitted; trailer already
                autoplays at the top of the modal. */}
            <div className="flex flex-wrap gap-3 md:gap-4">
              {release.streaming_url && !isReleaseUpcoming && (
                <a
                  href={release.streaming_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-watch-on-platform"
                  className="flex items-center gap-2 bg-white hover:bg-gray-200 text-black font-secondary font-bold px-4 md:px-6 py-2 md:py-3 rounded-full transition-all text-sm md:text-base"
                >
                  <span>Watch on {release.platform}</span>
                  <ExternalLink className="w-4 md:w-5 h-4 md:h-5" />
                </a>
              )}
              <button
                onClick={onToggleWatchlist}
                data-testid="detail-watchlist-toggle"
                className={`flex items-center gap-2 ${
                  isInWatchlist 
                    ? 'bg-red-500/20 text-red-500 border border-red-500/50' 
                    : 'bg-white/10 text-white border border-white/20'
                } font-secondary font-bold px-4 md:px-6 py-2 md:py-3 rounded-full transition-all text-sm md:text-base`}
              >
                <Heart className={`w-4 md:w-5 h-4 md:h-5 ${isInWatchlist ? 'fill-current' : ''}`} />
                <span>{isInWatchlist ? 'In Watchlist' : 'Add to Watchlist'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
